import React from "react";

/**
 * Self-contained fallback when a route chunk fails to load.
 * No lazy imports, icon fonts, images, or SCSS — must never throw.
 */
const WHATSAPP_URL = "https://wa.me/+27663784460";

const COLORS = {
  slate: "#4f6367",
  ink: "#2c3e40",
  orange: "#f17516",
  green: "#22a06b",
  paper: "#eef1f2",
  border: "#e2eaea",
  card: "#ffffff",
  page: "#f4f7f7",
};

function RefreshIllustration() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 120 120"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="160"
      height="160"
      aria-hidden="true"
      role="img"
    >
      <circle cx="60" cy="58" r="34" fill={COLORS.orange} opacity="0.08" />
      <path
        d="M60 28c16.6 0 30 13.4 30 30"
        stroke={COLORS.orange}
        strokeWidth="2.8"
        fill="none"
      />
      <path
        d="M86 52l4.5-6.5L84 41"
        stroke={COLORS.orange}
        strokeWidth="2.8"
        fill="none"
      />
      <path
        d="M60 88c-16.6 0-30-13.4-30-30"
        stroke={COLORS.slate}
        strokeWidth="2.2"
        strokeDasharray="4 5"
        opacity="0.45"
        fill="none"
      />
      <path
        d="M34 64l-4.5 6.5L36 75"
        stroke={COLORS.slate}
        strokeWidth="2.2"
        opacity="0.45"
        fill="none"
      />
      <rect
        x="42"
        y="34"
        width="36"
        height="58"
        rx="8"
        fill={COLORS.card}
        stroke={COLORS.slate}
        strokeWidth="2.2"
      />
      <rect x="54" y="38" width="12" height="3" rx="1.5" fill={COLORS.border} />
      <rect
        x="46"
        y="44"
        width="28"
        height="36"
        rx="4"
        fill={COLORS.paper}
        stroke={COLORS.slate}
        strokeWidth="1.6"
      />
      <path
        d="M50 62c4-7 9-10 15-7 6 3 10 1 13-5"
        stroke={COLORS.orange}
        strokeWidth="1.8"
        strokeDasharray="3 3"
        fill="none"
      />
      <circle cx="52" cy="60" r="2.8" fill={COLORS.slate} />
      <circle cx="68" cy="52" r="2.8" fill={COLORS.orange} />
      <rect
        x="50"
        y="48"
        width="20"
        height="14"
        rx="3.5"
        fill={COLORS.card}
        stroke={COLORS.slate}
        strokeWidth="1.5"
      />
      <text
        x="60"
        y="58"
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontSize="7.5"
        fontWeight="700"
        fill={COLORS.orange}
      >
        R 499
      </text>
      <rect x="50" y="84" width="20" height="8" rx="4" fill={COLORS.orange} />
      <path
        d="M18 92h10l2-3.5h6l1.5 3.5h4.5v4.5H18z"
        fill={COLORS.card}
        stroke={COLORS.slate}
        strokeWidth="1.5"
      />
      <rect x="18" y="91.5" width="10" height="1" fill={COLORS.orange} />
      <circle
        cx="21"
        cy="97.5"
        r="1.8"
        stroke={COLORS.slate}
        strokeWidth="1.2"
        fill={COLORS.paper}
      />
      <circle
        cx="29.5"
        cy="97.5"
        r="1.8"
        stroke={COLORS.slate}
        strokeWidth="1.2"
        fill={COLORS.paper}
      />
      <path
        d="M96 42l4-2.5M97 48h5.5M96 54l4 2.5"
        stroke={COLORS.green}
        strokeWidth="1.8"
      />
      <ellipse cx="60" cy="106" rx="28" ry="2.5" fill={COLORS.slate} opacity="0.08" />
    </svg>
  );
}

function TrustPill({ children }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "6px 12px",
        borderRadius: "999px",
        backgroundColor: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        color: COLORS.slate,
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: 1.3,
      }}
    >
      {children}
    </span>
  );
}

export default function ChunkErrorFallback({ onReload }) {
  const handleReload = () => {
    if (typeof onReload === "function") {
      onReload();
      return;
    }
    window.location.reload();
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        overflowY: "auto",
        backgroundColor: COLORS.page,
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: COLORS.ink,
      }}
    >
      <div
        style={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 20px 48px",
          boxSizing: "border-box",
        }}
      >
        <a
          href="/"
          aria-label="QikTruck home"
          style={{
            textDecoration: "none",
            marginBottom: "28px",
            fontSize: "22px",
            fontWeight: 800,
            letterSpacing: "-0.02em",
          }}
        >
          <span style={{ color: COLORS.orange }}>Qik</span>
          <span style={{ color: COLORS.slate }}>Truck</span>
        </a>

        <div
          style={{
            width: "100%",
            maxWidth: "440px",
            backgroundColor: COLORS.card,
            borderRadius: "16px",
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 8px 32px rgba(44, 62, 64, 0.08)",
            padding: "32px 24px 28px",
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          <RefreshIllustration />

          <h1
            style={{
              margin: "20px 0 10px",
              fontSize: "clamp(22px, 4vw, 26px)",
              fontWeight: 700,
              lineHeight: 1.25,
              color: COLORS.ink,
            }}
          >
            Almost there — refresh to continue
          </h1>

          <p
            style={{
              margin: "0 0 24px",
              fontSize: "16px",
              lineHeight: 1.55,
              color: COLORS.slate,
            }}
          >
            We&apos;ve rolled out an update. Refresh once to load the latest
            version and continue your quote or booking.
          </p>

          <button
            type="button"
            onClick={handleReload}
            style={{
              display: "inline-block",
              width: "100%",
              maxWidth: "280px",
              padding: "14px 24px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: "pointer",
              backgroundColor: COLORS.orange,
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(241, 117, 22, 0.35)",
            }}
          >
            Refresh page
          </button>

          <p
            style={{
              margin: "20px 0 0",
              fontSize: "14px",
              lineHeight: 1.5,
              color: COLORS.slate,
            }}
          >
            Need help now?{" "}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLORS.orange, fontWeight: 600, textDecoration: "none" }}
            >
              WhatsApp us
            </a>{" "}
            and we&apos;ll get your quote sorted.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "center",
            marginTop: "24px",
            maxWidth: "440px",
          }}
        >
          <TrustPill>Instant quotes</TrustPill>
          <TrustPill>Nationwide trucks</TrustPill>
          <TrustPill>Trusted drivers</TrustPill>
        </div>

        <a
          href="/"
          style={{
            marginTop: "20px",
            fontSize: "14px",
            color: COLORS.slate,
            textDecoration: "underline",
            textUnderlineOffset: "3px",
          }}
        >
          Back to homepage
        </a>
      </div>
    </div>
  );
}
